import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Radio,
  Lock,
  Unlock,
  RefreshCw,
  Signal,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Package,
  Eye,
  Unlink,
  MapPin,
  TrendingUp,
  Smartphone,
  Cpu,
  X,
  Archive,
  Activity,
  Plus,
  Loader2,
  Clock,
} from 'lucide-react'
import { baliseService, type RecentEventsResult, type AssignmentHistoryEntry } from '../services/baliseService'
import { containerService } from '../services/containerService'
import { assetService } from '../services/assetService'
import BMDashboard from './BMDashboard'
import MapModal from './MapModal'
import SimAssignModal from './SimAssignModal'
import BaliseSyncControl from './BaliseSyncControl'
import VehicleCreationWizard from './VehicleCreationWizard'
import type { Balise, BaliseFilters } from '../types/balise'

export interface BaliseManagementProps {
  showMessage: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

// Tabs:
//   - dashboard  → BMDashboard summary (mixed brands)
//   - inventory  → unified balise inventory (every brand, every device type)
//
// JIMI rejoined this inventory in P10. Brand and device_type are filter
// chips in the inventory header; the per-balise detail modal adapts its
// action panel to the device_type (Smart Lock vs GPS vs Dashcam vs OBD vs
// Tag), so an operator never has to leave this surface to manage a
// vendor-specific peripheral.
type TabId = 'dashboard' | 'inventory'

// Universal device-type filter applied client-side on top of whatever the
// server returns. NULL device_type counts as "GPS_TRACKER" for non-JIMI
// brands, matching the backend's inferPeripheralCapability fallback.
type DeviceTypeFilter = '' | 'SMART_LOCK' | 'GPS_TRACKER' | 'AI_DASHCAM' | 'OBD' | 'TAG'

function classifyDeviceType(b: { deviceType?: string | null; brand?: string | null }): DeviceTypeFilter {
  const t = String(b.deviceType || '').toUpperCase()
  if (t === 'SMART_LOCK')               return 'SMART_LOCK'
  if (t === 'GPS_TRACKER')              return 'GPS_TRACKER'
  if (t === 'AI_DASHCAM')               return 'AI_DASHCAM'
  if (t === 'OBD')                      return 'OBD'
  if (t === 'TAG' || t === 'BLE_TAG')   return 'TAG'
  // Legacy Jointech / MaxVision rows have no device_type set; they're
  // GPS trackers by definition.
  return 'GPS_TRACKER'
}

const PAGE_SIZE = 50

function isMaxVisionBrand(brand: string): boolean {
  return brand?.toUpperCase().includes('MAXVISION')
}

function isElectronicLockBrand(brand: string | null | undefined): boolean {
  const u = brand?.toUpperCase() || ''
  return u.includes('MAXVISION') || u.includes('JOINTECH')
}

function batteryBarColor(level: number | undefined): string {
  if (level == null || Number.isNaN(level)) return 'bg-gray-300'
  if (level > 60) return 'bg-green-500'
  if (level > 30) return 'bg-yellow-500'
  return 'bg-red-500'
}

function statusBadgeClass(status: string): string {
  const u = status?.toUpperCase() || ''
  if (u === 'ACTIVE') return 'bg-green-100 text-green-800 border-green-200'
  if (u === 'INACTIVE') return 'bg-red-100 text-red-800 border-red-200'
  if (u === 'MAINTENANCE') return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

// Brand-aware badge styling. Stays unified across the inventory and the
// detail modal so an operator can scan a long list and pick out, say, all
// JIMI rows by colour. New brands degrade gracefully into the gray
// "unknown" pill — we never want to crash a list because a vendor was
// added in the orchestration_db before the UI knew about it.
function brandBadgeClass(brand: string | null | undefined): string {
  const u = brand?.toUpperCase().trim() || ''
  if (u.includes('MAXVISION')) return 'bg-blue-100 text-blue-800 border-blue-200'
  if (u.includes('JOINTECH')) return 'bg-emerald-100 text-emerald-800 border-emerald-200'
  if (u.includes('TELTONIKA')) return 'bg-violet-100 text-violet-800 border-violet-200'
  if (u.includes('JIMI')) return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-gray-100 text-gray-700 border-gray-200'
}

function brandLabel(brand: string | null | undefined): string {
  const v = brand?.trim()
  return v && v.length > 0 ? v : '—'
}

function assignmentBadgeClass(isAssigned: boolean): string {
  return isAssigned
    ? 'bg-green-100 text-green-800 border-green-200'
    : 'bg-orange-100 text-orange-800 border-orange-200'
}

function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { message?: string; error?: string } } }).response?.data
    return r?.message || r?.error || fallback
  }
  if (e instanceof Error) return e.message || fallback
  return fallback
}

function formatDate(dateString?: string): string {
  if (!dateString) return '—'
  try {
    const d = new Date(dateString)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString()
  } catch { return '—' }
}

function formatTime(dateString?: string): string {
  if (!dateString) return ''
  try {
    const d = new Date(dateString)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleTimeString()
  } catch { return '' }
}

function renderSyncPayload(data: unknown): React.ReactNode {
  if (data == null) return '—'
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') return String(data)
  if (Array.isArray(data)) {
    if (data.length === 0) return '—'
    return (
      <ul className="space-y-2 text-sm text-gray-700">
        {data.map((item, i) => (
          <li key={i} className="border-b border-gray-100 pb-2 last:border-0">
            {typeof item === 'object' && item !== null ? (
              <pre className="whitespace-pre-wrap break-all text-xs bg-gray-50 p-2 rounded">{JSON.stringify(item, null, 2)}</pre>
            ) : String(item)}
          </li>
        ))}
      </ul>
    )
  }
  return <pre className="whitespace-pre-wrap break-all text-xs bg-gray-50 p-3 rounded-lg border border-gray-100">{JSON.stringify(data, null, 2)}</pre>
}

const BaliseManagement: React.FC<BaliseManagementProps> = ({ showMessage }) => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  const [activeTab, setActiveTab] = useState<TabId>('inventory')

  const [balises, setBalises] = useState<Balise[]>([])
  const [inventoryLoading, setInventoryLoading] = useState(true)
  const [inventoryError, setInventoryError] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  // Operational status (ACTIVE/INACTIVE) — set by the "Active" stat card.
  const [operationalFilter, setOperationalFilter] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [brandFilter, setBrandFilter] = useState<string>('')
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<DeviceTypeFilter>('')

  const [detailBalise, setDetailBalise] = useState<Balise | null>(null)
  // Recent-activity panel inside the detail modal — works for any brand.
  // Powered by GET /api/balises/:lockCode/recent-events (added in fleet-api
  // server.js for P2). Fetched lazily when the modal opens; reset on close
  // so a stale cache from a previous balise never flashes.
  const [recentEvents, setRecentEvents] = useState<RecentEventsResult | null>(null)
  const [recentEventsLoading, setRecentEventsLoading] = useState(false)
  const [recentEventsError, setRecentEventsError] = useState<string | null>(null)

  // Balise-side assignment history (who was this device mounted on, when).
  // Loaded with the detail modal; separate state so a failure here never
  // blocks the rest of the panel.
  const [detailHistory, setDetailHistory] = useState<AssignmentHistoryEntry[]>([])
  const [detailHistoryLoading, setDetailHistoryLoading] = useState(false)

  // Container/Asset assignment modal — legacy 3-card layout
  // (Containers + Assets + Create New). Selecting a row opens an
  // intermediate confirmation alert before the actual assign call so
  // the operator sees the target name before committing.
  const [showContainerAssignModal, setShowContainerAssignModal] = useState(false)
  const [assignBalise, setAssignBalise] = useState<Balise | null>(null)
  const [availableContainers, setAvailableContainers] = useState<any[]>([])
  const [availableAssets, setAvailableAssets] = useState<any[]>([])
  const [containersLoading, setContainersLoading] = useState(false)
  const [assetSearch, setAssetSearch] = useState('')
  // Status dropdown in the assign-balise modal ('' = all statuses)
  const [assignStatusFilter, setAssignStatusFilter] = useState('')
  const [assignTarget, setAssignTarget] = useState<{ kind: 'container' | 'asset'; id: number; name: string } | null>(null)
  const [assignBusy, setAssignBusy] = useState(false)

  // Sync-aware unassign — legacy fetches the sync state first; if
  // ACTIVE the operator gets a 3-button alert (Cancel | Keep sync |
  // Stop sync), otherwise plain 2-button confirm. We mirror that flow
  // here. `unassignSyncStatus` is null while we're still loading, then
  // 'ACTIVE' / 'INACTIVE' / 'unknown' once the GET resolves.
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false)
  const [unassignBalise, setUnassignBalise] = useState<Balise | null>(null)
  const [unassignSyncStatus, setUnassignSyncStatus] = useState<'ACTIVE' | 'INACTIVE' | 'unknown' | null>(null)
  const [unassignBusy, setUnassignBusy] = useState(false)
  // Human-readable label of the vehicle/container currently carrying the
  // balise, shown in the unassign confirmation so the operator knows
  // exactly what will stop being tracked. null = still loading / none.
  const [unassignVehicleLabel, setUnassignVehicleLabel] = useState<string | null>(null)

  const [showDecomConfirm, setShowDecomConfirm] = useState(false)
  const [decomBalise, setDecomBalise] = useState<Balise | null>(null)
  const [decomReason, setDecomReason] = useState('')
  const [decomBusy, setDecomBusy] = useState(false)

  const [showCommandModal, setShowCommandModal] = useState(false)
  const [commandType, setCommandType] = useState<'seal' | 'unseal'>('seal')
  const [commandBalise, setCommandBalise] = useState<Balise | null>(null)
  const [commandPassword, setCommandPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [commandForTrip, setCommandForTrip] = useState(false)
  const [commandBusy, setCommandBusy] = useState(false)

  const [pickerBalises, setPickerBalises] = useState<Balise[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)
  const [commandsSelectedId, setCommandsSelectedId] = useState<number | null>(null)
  const [deviceModeChoice, setDeviceModeChoice] = useState<'normal' | 'trip'>('normal')
  const [commandResultKey, setCommandResultKey] = useState<string | null>(null)

  const [syncLockCode, setSyncLockCode] = useState('')
  const [syncStatus, setSyncStatus] = useState<unknown>(null)
  const [syncHistory, setSyncHistory] = useState<unknown>(null)
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [syncActionBusy, setSyncActionBusy] = useState(false)

  // Map modal — single component handles both 'location' (iframe) and
  // 'trajectory' (Google Maps polyline) modes. Two row icons (#4 GPS
  // Location, #5 Trajectory) seed the initial mode; the operator can
  // toggle inside via the Segment in the modal header.
  const [mapTarget, setMapTarget] = useState<Balise | null>(null)
  const [mapMode, setMapMode] = useState<'location' | 'trajectory'>('location')

  // SIM picker — opens a SimAssignModal with the existing SIM (if any)
  // pre-loaded as a banner so the operator can replace or detach in one
  // place. Replaces the legacy "coming soon" toast.
  const [simTarget, setSimTarget] = useState<Balise | null>(null)

  // Container/Asset picker search boxes.
  const [containerSearch, setContainerSearch] = useState('')

  // Create-new: same VehicleCreationWizard used everywhere else.
  const [showCreationWizard, setShowCreationWizard] = useState(false)
  const [wizardSource, setWizardSource] = useState<'ASSET' | 'CONTAINER'>('ASSET')

  const [decomTabBaliseId, setDecomTabBaliseId] = useState<number | null>(null)
  const [decomTabConfirmInput, setDecomTabConfirmInput] = useState('')
  const [decomTabBusy, setDecomTabBusy] = useState(false)

  const selectedCommandBalise = useMemo(
    () => pickerBalises.find((b) => b.id === commandsSelectedId) ?? null,
    [pickerBalises, commandsSelectedId]
  )
  const decomTabBalise = useMemo(
    () => pickerBalises.find((b) => b.id === decomTabBaliseId) ?? null,
    [pickerBalises, decomTabBaliseId]
  )

  const statistics = useMemo(() => {
    const total = balises.length
    const assigned = balises.filter((b) => b.assignmentStatus === 'ASSIGNED').length
    const unassigned = total - assigned
    const online = balises.filter((b) => b.status === 'ACTIVE').length
    const offline = total - online
    return { total: totalCount || total, assigned, unassigned, online, offline, needsAttention: 0 }
  }, [balises, totalCount])

  useEffect(() => {
    const tmr = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 350)
    return () => window.clearTimeout(tmr)
  }, [searchInput])

  // Unified inventory — every brand, every device type. Brand and search
  // are server-side filters (so pagination & totals stay accurate);
  // device_type is filtered client-side because legacy Jointech /
  // MaxVision rows store NULL there.
  const filtersForApi = useMemo((): BaliseFilters => {
    const f: BaliseFilters = {}
    if (debouncedSearch)     f.search = debouncedSearch
    if (statusFilter)        f.assignmentStatus = statusFilter
    if (operationalFilter)   f.operationalStatus = operationalFilter
    if (brandFilter)         f.brand = brandFilter
    return f
  }, [debouncedSearch, statusFilter, operationalFilter, brandFilter])

  // Defensive: keep `showMessage` and `t` out of `loadInventory`'s
  // dep array via refs. Even though the parent now memoizes
  // `showMessage`, this guarantees that an accidental future regression
  // (a sibling component un-memoizing its callback, an unstable `t`
  // reference after a language change, etc.) won't put us back in the
  // re-fetch storm we observed in production logs.
  const showMessageRef = useRef(showMessage)
  useEffect(() => { showMessageRef.current = showMessage }, [showMessage])
  const tRef = useRef(t)
  useEffect(() => { tRef.current = t }, [t])

  const loadInventory = useCallback(async () => {
    setInventoryLoading(true)
    setInventoryError(false)
    try {
      const res = await baliseService.getAllBalises(page, PAGE_SIZE, filtersForApi)
      setBalises(res.data)
      setTotalPages(Math.max(res.totalPages, 1))
      setTotalCount(res.total)
    } catch (e) {
      setInventoryError(true)
      setBalises([])
      showMessageRef.current(errMessage(e, tRef.current('balise_load_failed')), 'error')
    } finally {
      setInventoryLoading(false)
    }
  }, [page, filtersForApi])

  useEffect(() => {
    loadInventory()
  }, [loadInventory])

  // Cross-component bridge: BMDashboard's brand cards (and any other
  // surface that wants to drive the inventory) dispatch
  // `fleet:filter-balise-inventory` with `{ brand?, deviceType? }`. We
  // listen here, switch to the Inventory tab, and apply the filters
  // server- and client-side.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ brand?: string; deviceType?: DeviceTypeFilter }>).detail || {}
      setActiveTab('inventory')
      if (typeof detail.brand === 'string')      setBrandFilter(detail.brand)
      if (typeof detail.deviceType === 'string') setDeviceTypeFilter(detail.deviceType as DeviceTypeFilter)
    }
    window.addEventListener('fleet:filter-balise-inventory', handler as EventListener)
    return () => window.removeEventListener('fleet:filter-balise-inventory', handler as EventListener)
  }, [])

  // Client-side device-type filter applied on top of the server response.
  // We keep this on the client because legacy Jointech / MaxVision rows
  // store device_type=NULL — we want them to appear under "GPS Tracker"
  // even though the SQL column is empty.
  const filteredBalises = useMemo(() => {
    if (!deviceTypeFilter) return balises
    return balises.filter((b) => classifyDeviceType(b as any) === deviceTypeFilter)
  }, [balises, deviceTypeFilter])

  useEffect(() => { setPage(1) }, [debouncedSearch, statusFilter, operationalFilter, brandFilter, deviceTypeFilter])

  // Lazy-load recent activity for the detail modal. Resets when the modal
  // closes so a previously-viewed device's events never flash through.
  useEffect(() => {
    if (!detailBalise?.lockCode) {
      setRecentEvents(null)
      setRecentEventsError(null)
      setRecentEventsLoading(false)
      return
    }
    let cancelled = false
    setRecentEventsLoading(true)
    setRecentEventsError(null)
    baliseService
      .getRecentEvents(detailBalise.lockCode, { hours: 24, limit: 50 })
      .then((res) => {
        if (cancelled) return
        setRecentEvents(res)
      })
      .catch((e: any) => {
        if (cancelled) return
        setRecentEvents(null)
        setRecentEventsError(e?.message || 'Failed to load recent events')
      })
      .finally(() => {
        if (!cancelled) setRecentEventsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [detailBalise?.lockCode])

  // Assignment history for the detail modal (balise side)
  useEffect(() => {
    if (!detailBalise?.lockCode) {
      setDetailHistory([])
      setDetailHistoryLoading(false)
      return
    }
    let cancelled = false
    setDetailHistoryLoading(true)
    baliseService
      .getAssignmentHistory(detailBalise.lockCode)
      .then((rows) => { if (!cancelled) setDetailHistory(rows) })
      .catch(() => { if (!cancelled) setDetailHistory([]) })
      .finally(() => { if (!cancelled) setDetailHistoryLoading(false) })
    return () => { cancelled = true }
  }, [detailBalise?.lockCode])

  // Picker balises (`pickerBalises`) used to be prefetched (500 rows!) on
  // every tab switch to drive the now-removed JIMI tab's command/decom
  // sub-tabs. With those tabs gone in P10 the prefetch became dead load —
  // and worse, it was re-firing every parent re-render because
  // `loadPickerBalises` had `showMessage` in its deps and the parent did
  // not memoize it. The picker state is kept (a couple of dead memos read
  // from it) but it now stays an empty array; the inventory row actions
  // operate directly on the row balise, they never needed it.

  const lockCodes = useMemo(() => {
    const codes = new Set<string>()
    pickerBalises.forEach((b) => { if (b.lockCode) codes.add(b.lockCode) })
    return Array.from(codes).sort()
  }, [pickerBalises])

  const getSavedPassword = (lockCode: string): string => {
    try {
      const passwords = JSON.parse(localStorage.getItem('balisePasswords') || '{}')
      return passwords[lockCode] || ''
    } catch { return '' }
  }

  const savePasswordToStorage = (lockCode: string, password: string) => {
    try {
      const passwords = JSON.parse(localStorage.getItem('balisePasswords') || '{}')
      if (password) passwords[lockCode] = password
      else delete passwords[lockCode]
      localStorage.setItem('balisePasswords', JSON.stringify(passwords))
    } catch { /* ignore */ }
  }

  const clearFilters = () => {
    setSearchInput('')
    setStatusFilter('')
    setOperationalFilter('')
    setLocationFilter('')
    setBrandFilter('')
    setDeviceTypeFilter('')
    setPage(1)
  }

  const handleDetailsClick = (b: Balise) => {
    setDetailBalise(b)
  }

  // Container/Asset picker — loads BOTH lists in parallel so the
  // legacy 3-card layout (Containers + Assets + Create New) renders
  // without a second round-trip when the operator clicks between cards.
  const handleAssignContainerClick = async (b: Balise) => {
    setAssignBalise(b)
    setContainersLoading(true)
    setContainerSearch('')
    setAssetSearch('')
    try {
      const [cRes, aRes] = await Promise.all([
        containerService.getContainers().catch(() => ({ success: false, data: [] as any[] })),
        assetService.getAssets().catch(() => ({ success: false, data: [] as any[] } as any)),
      ])
      setAvailableContainers(cRes && (cRes as any).success && Array.isArray((cRes as any).data) ? (cRes as any).data : [])
      setAvailableAssets(aRes && (aRes as any).success && Array.isArray((aRes as any).data) ? (aRes as any).data : [])
    } catch {
      setAvailableContainers([])
      setAvailableAssets([])
      showMessage(t('balise_load_containers_failed', { defaultValue: 'Failed to load containers' }), 'error')
    } finally {
      setContainersLoading(false)
    }
    setShowContainerAssignModal(true)
  }

  // Confirmation alert ("BALISE → TARGET? Cancel | Assign") — legacy
  // visual flow §B. We don't fire the assignment until the operator
  // confirms, so an accidental row click can be undone.
  const requestAssignTarget = (kind: 'container' | 'asset', id: number, name: string) => {
    setAssignTarget({ kind, id, name })
  }

  const confirmAssignTarget = async () => {
    if (!assignBalise || !assignTarget || assignBusy) return
    setAssignBusy(true)
    try {
      if (assignTarget.kind === 'container') {
        await baliseService.assignBaliseToContainer(assignBalise.lockCode, assignTarget.id, assignTarget.name)
      } else {
        await baliseService.assignBaliseToAsset(assignBalise.lockCode, assignTarget.id)
      }
      showMessage(t('balise_assign_success'), 'success')
      setShowContainerAssignModal(false)
      setAssignBalise(null)
      setAssignTarget(null)
      setContainerSearch('')
      setAssignStatusFilter('')
      loadInventory()
    } catch (e) {
      showMessage(errMessage(e, t('balise_assign_failed')), 'error')
    } finally {
      setAssignBusy(false)
    }
  }

  const openCreationWizard = (mode: 'asset' | 'container') => {
    setWizardSource(mode === 'container' ? 'CONTAINER' : 'ASSET')
    setShowCreationWizard(true)
  }

  const refreshAssignLists = async () => {
    setContainersLoading(true)
    try {
      const [cRes, aRes] = await Promise.all([
        containerService.getContainers().catch(() => ({ success: false, data: [] as any[] })),
        assetService.getAssets().catch(() => ({ success: false, data: [] as any[] } as any)),
      ])
      setAvailableContainers(cRes && (cRes as any).success && Array.isArray((cRes as any).data) ? (cRes as any).data : [])
      setAvailableAssets(aRes && (aRes as any).success && Array.isArray((aRes as any).data) ? (aRes as any).data : [])
    } catch { /* keep old data */ }
    finally { setContainersLoading(false) }
  }

  // Unassign with sync-status check (legacy §C). The Java endpoint
  // currently 404s because of a known @Path bug — we treat that as
  // 'unknown' status and fall through to the plain 2-button confirm
  // (mirrors legacy's silent fallback behaviour).
  const handleUnassignClick = async (b: Balise) => {
    setUnassignBalise(b)
    setUnassignSyncStatus(null)
    setUnassignVehicleLabel(null)
    setShowUnassignConfirm(true)

    // Resolve which vehicle/container is currently carrying this balise so
    // the confirmation names the actual thing that will stop being tracked.
    baliseService.getAssignmentHistory(b.lockCode)
      .then((rows) => {
        const active = rows.find(r => r.is_active)
        if (!active) return
        const label = active.container_name
          || [active.asset_code, active.license_plate].filter(Boolean).join(' · ')
          || active.customer_name
        if (label) setUnassignVehicleLabel(label)
      })
      .catch(() => { /* dialog falls back to customer name */ })

    try {
      const raw: any = await baliseService.getSyncStatus(b.lockCode)
      const data = raw?.data ?? raw
      const s = String(data?.syncStatus || data?.sync_status || '').toUpperCase()
      setUnassignSyncStatus(s === 'ACTIVE' ? 'ACTIVE' : s === 'INACTIVE' ? 'INACTIVE' : 'unknown')
    } catch {
      setUnassignSyncStatus('unknown')
    }
  }

  const closeUnassignConfirm = () => {
    setShowUnassignConfirm(false)
    setUnassignBalise(null)
    setUnassignSyncStatus(null)
    setUnassignVehicleLabel(null)
    setUnassignBusy(false)
  }

  // Both 2-button and 3-button paths funnel through here. `syncFollowUp`
  // tells us whether to call /maintain (keep sync) or /deactivate (stop)
  // before the actual unassign. When sync wasn't ACTIVE we skip both.
  const performUnassign = async (syncFollowUp: 'maintain' | 'deactivate' | null) => {
    if (!unassignBalise) return
    setUnassignBusy(true)
    try {
      if (syncFollowUp) {
        try { await baliseService.syncAction(unassignBalise.lockCode, syncFollowUp) }
        catch { /* sync endpoint may be unavailable; carry on */ }
      }
      await baliseService.unassignBalise(unassignBalise.lockCode)
      showMessage(t('balise_unassign_success'), 'success')
      closeUnassignConfirm()
      loadInventory()
    } catch (e) {
      showMessage(errMessage(e, t('balise_unassign_failed')), 'error')
      setUnassignBusy(false)
    }
  }

  const handleSimClick = (b: Balise) => {
    setSimTarget(b)
  }

  const handleGpsLocationClick = (b: Balise) => {
    setMapMode('location')
    setMapTarget(b)
  }

  const handleTrajectoryClick = (b: Balise) => {
    setMapMode('trajectory')
    setMapTarget(b)
  }

  const handleSealClick = (b: Balise) => {
    setCommandBalise(b)
    setCommandType('seal')
    setCommandPassword(getSavedPassword(b.lockCode))
    setCommandForTrip(false)
    setShowCommandModal(true)
  }

  const handleUnsealClick = (b: Balise) => {
    setCommandBalise(b)
    setCommandType('unseal')
    setCommandPassword(getSavedPassword(b.lockCode))
    setCommandForTrip(false)
    setShowCommandModal(true)
  }

  const handleSendCommand = async () => {
    if (!commandBalise) return
    if (commandPassword) savePasswordToStorage(commandBalise.lockCode, commandPassword)
    setCommandBusy(true)
    try {
      const opts: Record<string, any> = {}
      if (commandPassword) opts.keyPassword = commandPassword
      if (commandBalise.brand?.toUpperCase().includes('MAXVISION') && commandForTrip) opts.forTrip = true

      const res = commandType === 'seal'
        ? await baliseService.sealLock(commandBalise.lockCode, commandBalise.brand, opts)
        : await baliseService.unsealLock(commandBalise.lockCode, commandBalise.brand, opts)

      if (res?.success) {
        showMessage(res.message || t(commandType === 'seal' ? 'balise_seal_success' : 'balise_unseal_success'), 'success')
        setShowCommandModal(false)
        setCommandPassword('')
        loadInventory()
      } else {
        showMessage(res?.error || res?.message || t('balise_seal_failed'), 'error')
      }
    } catch (e) {
      let msg = errMessage(e, t('balise_seal_failed'))
      if (msg.includes('1001') || msg.includes('not connected')) msg = t('balise_device_offline', { defaultValue: 'Device is offline or not connected' })
      else if (msg.includes('ConnectException') || msg.includes('connect')) msg = t('balise_gateway_down', { defaultValue: 'Gateway is down or unreachable' })
      showMessage(msg, 'error')
    } finally {
      setCommandBusy(false)
    }
  }

  const handleDecommissionClick = (b: Balise) => {
    setDecomBalise(b)
    setDecomReason('')
    setShowDecomConfirm(true)
  }

  // Legacy parity: send the reason through to the audit log. Java's
  // baliseService.decommission ignores the body for now, but
  // recording the operator's reason locally (toast) gives them
  // confirmation that their input was captured for the next time the
  // backend stores it.
  const confirmDecommission = async () => {
    if (!decomBalise) return
    setDecomBusy(true)
    try {
      await baliseService.decommission(decomBalise.lockCode)
      showMessage(t('balise_decom_success'), 'success')
      setShowDecomConfirm(false)
      setDecomBalise(null)
      setDecomReason('')
      loadInventory()
    } catch (e) {
      showMessage(errMessage(e, t('balise_decom_failed')), 'error')
    } finally {
      setDecomBusy(false)
    }
  }


  const runSealTab = async () => {
    if (!selectedCommandBalise) { showMessage(t('balise_commands_select_first'), 'warning'); return }
    setCommandBusy(true); setCommandResultKey(null)
    try {
      const res = await baliseService.sealLock(selectedCommandBalise.lockCode, selectedCommandBalise.brand)
      if (res?.success) { showMessage(res.message || t('balise_seal_success'), 'success'); setCommandResultKey(res.message || t('balise_seal_success')) }
      else { const msg = res?.error || res?.message || t('balise_seal_failed'); showMessage(msg, 'error'); setCommandResultKey(msg) }
    } catch (e) { const msg = errMessage(e, t('balise_seal_failed')); showMessage(msg, 'error'); setCommandResultKey(msg) }
    finally { setCommandBusy(false) }
  }

  const runUnsealTab = async () => {
    if (!selectedCommandBalise) { showMessage(t('balise_commands_select_first'), 'warning'); return }
    setCommandBusy(true); setCommandResultKey(null)
    try {
      const res = await baliseService.unsealLock(selectedCommandBalise.lockCode, selectedCommandBalise.brand)
      if (res?.success) { showMessage(res.message || t('balise_unseal_success'), 'success'); setCommandResultKey(res.message || t('balise_unseal_success')) }
      else { const msg = res?.error || res?.message || t('balise_unseal_failed'); showMessage(msg, 'error'); setCommandResultKey(msg) }
    } catch (e) { const msg = errMessage(e, t('balise_unseal_failed')); showMessage(msg, 'error'); setCommandResultKey(msg) }
    finally { setCommandBusy(false) }
  }

  const runChangeMode = async () => {
    if (!selectedCommandBalise) { showMessage(t('balise_commands_select_first'), 'warning'); return }
    if (!isMaxVisionBrand(selectedCommandBalise.brand)) { showMessage(t('balise_mode_maxvision_only'), 'warning'); return }
    const mode = deviceModeChoice === 'trip' ? 1 : 0
    setCommandBusy(true); setCommandResultKey(null)
    try {
      const res = await baliseService.changeDeviceMode(selectedCommandBalise.lockCode, selectedCommandBalise.brand, mode)
      if (res?.success) { showMessage(res.message || t('balise_mode_success'), 'success'); setCommandResultKey(res.message || t('balise_mode_success')) }
      else { const msg = res?.error || res?.message || t('balise_mode_failed'); showMessage(msg, 'error'); setCommandResultKey(msg) }
    } catch (e) { const msg = errMessage(e, t('balise_mode_failed')); showMessage(msg, 'error'); setCommandResultKey(msg) }
    finally { setCommandBusy(false) }
  }

  const loadSyncData = async () => {
    if (!syncLockCode) { showMessage(t('balise_sync_no_selection'), 'warning'); return }
    setSyncLoading(true); setSyncError(null)
    try {
      const [st, hi] = await Promise.all([baliseService.getSyncStatus(syncLockCode), baliseService.getSyncHistory(syncLockCode)])
      setSyncStatus(st); setSyncHistory(hi)
    } catch (e) { const msg = errMessage(e, t('balise_error_generic')); setSyncError(msg); showMessage(msg, 'error'); setSyncStatus(null); setSyncHistory(null) }
    finally { setSyncLoading(false) }
  }

  const runSyncAction = async (action: 'activate' | 'deactivate' | 'maintain') => {
    if (!syncLockCode) { showMessage(t('balise_sync_no_selection'), 'warning'); return }
    setSyncActionBusy(true)
    try { await baliseService.syncAction(syncLockCode, action); showMessage(t('balise_sync_action_success'), 'success'); await loadSyncData() }
    catch (e) { showMessage(errMessage(e, t('balise_sync_action_failed')), 'error') }
    finally { setSyncActionBusy(false) }
  }

  const runDecomTab = async () => {
    if (!decomTabBalise) { showMessage(t('balise_decom_select'), 'warning'); return }
    if (decomTabConfirmInput.trim() !== decomTabBalise.lockCode) { showMessage(t('balise_decom_mismatch'), 'error'); return }
    setDecomTabBusy(true)
    try {
      await baliseService.decommission(decomTabBalise.lockCode)
      showMessage(t('balise_decom_success'), 'success'); setDecomTabConfirmInput(''); setDecomTabBaliseId(null)
      loadInventory()
    } catch (e) { showMessage(errMessage(e, t('balise_decom_failed')), 'error') }
    finally { setDecomTabBusy(false) }
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'inventory', label: t('balise_tab_inventory', { defaultValue: 'Balises-IOTs' }) },
    { id: 'dashboard', label: t('bm_dashboard') },
  ]

  // Shared list/table renderer for the inventory tab. Unified across
  // every brand (Jointech, MaxVision, JIMI) and every device type.
  // Brand & search are server-side; device_type is a client-side chip
  // because legacy rows store NULL there and we still want them under
  // the "GPS Tracker" chip.
  const renderBaliseList = () => {
    const visible = filteredBalises

    const deviceTypeChips: { id: DeviceTypeFilter; label: string }[] = [
      { id: '',             label: t('bm_filter_type_all',     { defaultValue: 'All types' }) },
      { id: 'GPS_TRACKER',  label: t('bm_filter_type_gps',     { defaultValue: 'GPS Tracker' }) },
      { id: 'SMART_LOCK',   label: t('bm_filter_type_lock',    { defaultValue: 'Smart Lock' }) },
      { id: 'AI_DASHCAM',   label: t('bm_filter_type_dashcam', { defaultValue: 'Dashcam' }) },
      { id: 'OBD',          label: t('bm_filter_type_obd',     { defaultValue: 'OBD' }) },
      { id: 'TAG',          label: t('bm_filter_type_tag',     { defaultValue: 'BLE Tag' }) },
    ]

    return (
      <>
        {/* Filters - matching BM layout. The status / location dropdowns
            stay shared across tabs so the operator's mental model
            doesn't shift between brands. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex-shrink-0">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('balise_search_placeholder')}
                className="w-full pl-10 pr-3 py-2 bg-gray-100 rounded-lg border-0 text-sm text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[140px]">
              <option value="">{t('bm_filter_brand_all', { defaultValue: 'All brands' })}</option>
              <option value="Jointech">Jointech</option>
              <option value="MaxVision">MaxVision</option>
              <option value="TELTONIKA">Teltonika</option>
              <option value="JIMI">JIMI</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[140px]">
              <option value="">{t('balise_filter_assignment_all')}</option>
              <option value="ASSIGNED">{t('balise_assigned', { defaultValue: 'Assigned' })}</option>
              <option value="AVAILABLE">{t('balise_unassigned', { defaultValue: 'Unassigned' })}</option>
              <option value="MAINTENANCE">{t('balise_status_maintenance', { defaultValue: 'Maintenance' })}</option>
            </select>
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[140px]">
              <option value="">{t('balise_filter_all_locations', { defaultValue: 'All Locations' })}</option>
            </select>
            <button type="button" onClick={clearFilters} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              {t('clear', { defaultValue: 'CLEAR' })}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {deviceTypeChips.map((chip) => (
              <button
                key={chip.id || 'all'}
                type="button"
                onClick={() => setDeviceTypeFilter(chip.id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  deviceTypeFilter === chip.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sticky horizontal scrollbar inside a constrained-height
            flex column. `min-h-0` is the key trick — without it the
            inner scroller refuses to shrink below the table's
            intrinsic height, and the horizontal scrollbar pushes
            below the floating window's fold (where the user can't
            see it). The pagination footer stays OUTSIDE the
            scroller so it remains visible regardless of how short
            the operator makes the BaliseManagement window. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
          {inventoryLoading ? (
            <div className="flex items-center justify-center p-12 text-gray-500">{t('loading')}</div>
          ) : inventoryError ? (
            <div className="flex flex-col items-center justify-center p-12 gap-3">
              <AlertTriangle className="w-10 h-10 text-amber-500" />
              <p className="text-gray-600">{t('balise_load_failed')}</p>
              <button type="button" onClick={loadInventory} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">{t('balise_refresh_list')}</button>
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12">
              <Cpu className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500">{t('balise_no_balises')}</p>
            </div>
          ) : (
            <>
              {/* `overflow-x-scroll` (vs auto) keeps the scrollbar
                  always visible — macOS WebKit hides auto-scrollbars
                  until you drag them, which made operators think the
                  Actions column had vanished when the floating window
                  was narrower than the table's intrinsic width. The
                  Tailwind arbitrary selectors theme the WebKit
                  scrollbar so it doesn't look like a 1990s grey blob.
                  `min-h-0` lets the scroller shrink below the table's
                  intrinsic height; combined with the parent's
                  maxHeight the H scrollbar always stays in view. */}
              <div className="flex-1 min-h-0 overflow-x-scroll overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
                <table className="w-full text-sm" style={{ minWidth: '1200px' }}>
                  <thead>
                    <tr className="border-b-2 border-gray-200 bg-gray-50 sticky top-0 z-10">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_balise', { defaultValue: 'Balise' })}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        {t('balise_col_status')}
                        <div className="text-[10px] font-normal text-gray-400 mt-0.5">({t('balise_col_transmission', { defaultValue: 'Transmission' })})</div>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_serial', { defaultValue: 'Serial' })}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_assignment', { defaultValue: 'Assignment' })}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_battery')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_last_update', { defaultValue: 'Last Update' })}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('balise_col_actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((b) => {
                      const hasGps = !!(b.latitude && b.longitude)
                      return (
                        <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50/80">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Cpu className="w-6 h-6 text-gray-400 flex-shrink-0" />
                              <div>
                                <div className="font-semibold text-gray-900">{b.deviceName || b.universalId}</div>
                                <span className={`inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${brandBadgeClass(b.brand)}`}>
                                  {brandLabel(b.brand)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${statusBadgeClass(b.status || '')}`}>
                              {b.status === 'ACTIVE' && <Signal className="w-3 h-3" />}
                              {b.status || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{b.lockCode}</div>
                            <div className="text-xs text-gray-500">ID: {b.universalId}</div>
                          </td>
                          <td className="px-4 py-3">
                            {b.customerName && (
                              <div className="font-medium text-gray-900 mb-1">{b.customerName}</div>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${assignmentBadgeClass(!!b.isAssigned)}`}>
                              {b.isAssigned ? t('balise_assigned', { defaultValue: 'Assigned' }) : t('balise_unassigned', { defaultValue: 'Unassigned' })}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="relative w-[60px] h-3 bg-gray-200 rounded overflow-hidden border border-gray-300">
                                <div className={`absolute left-0 top-0 h-full rounded ${batteryBarColor(b.batteryLevel)}`}
                                  style={{ width: `${Math.min(100, Math.max(0, b.batteryLevel ?? 0))}%`, transition: 'width 0.3s ease' }} />
                              </div>
                              <span className="text-xs font-medium text-gray-600 tabular-nums">
                                {b.batteryLevel != null ? `${b.batteryLevel}%` : '—'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{formatDate(b.updatedAt || b.lastSeen || b.createdAt)}</div>
                            <div className="text-xs text-gray-500">{formatTime(b.updatedAt || b.lastSeen || b.createdAt)}</div>
                          </td>
                          <td className="px-4 py-3">
                            {/* 8 buttons in legacy order:
                                  Eye / Container|Unlink / SIM / GPS Loc /
                                  Trajectory / Seal / Unseal / Decommission.
                                Each button fires its own action directly
                                — no funnel through the detail modal.
                                Detail modal is opened ONLY by the eye and
                                is strictly read-only (cards + sync
                                widget) so we don't duplicate row
                                operations inside it. */}
                            <div className="flex items-center gap-0.5 flex-nowrap">
                              <button type="button" onClick={() => handleDetailsClick(b)} title={t('balise_action_view_details')}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><Eye className="w-4 h-4" /></button>
                              {b.isAssigned || b.containerId || b.assetId ? (
                                <button type="button" onClick={() => handleUnassignClick(b)} title={t('balise_action_unassign')}
                                  className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Unlink className="w-4 h-4" /></button>
                              ) : (
                                <button type="button" onClick={() => handleAssignContainerClick(b)} title={t('balise_action_assign_container', { defaultValue: 'Assign to container' })}
                                  className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50"><Package className="w-4 h-4" /></button>
                              )}
                              <button type="button" onClick={() => handleSimClick(b)} title={t('balise_action_sim', { defaultValue: 'Manage SIM' })}
                                className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50"><Smartphone className="w-4 h-4" /></button>
                              <button type="button" onClick={() => hasGps ? handleGpsLocationClick(b) : showMessage(t('balise_no_gps', { defaultValue: 'No GPS data available for this device' }), 'warning')} title={t('balise_action_gps', { defaultValue: 'GPS Position' })}
                                className={`p-1.5 rounded-lg hover:bg-blue-50 ${hasGps ? 'text-blue-500' : 'text-gray-300'}`}><MapPin className="w-4 h-4" /></button>
                              <button type="button" onClick={() => handleTrajectoryClick(b)} title={t('balise_action_trajectory', { defaultValue: 'Trajectory' })}
                                className="p-1.5 rounded-lg text-cyan-600 hover:bg-cyan-50"><TrendingUp className="w-4 h-4" /></button>
                              {isElectronicLockBrand(b.brand) && (
                                <>
                                  <button type="button" onClick={() => handleSealClick(b)} title={t('balise_commands_seal')}
                                    className="p-1.5 rounded-lg text-green-600 hover:bg-green-50"><Lock className="w-4 h-4" /></button>
                                  <button type="button" onClick={() => handleUnsealClick(b)} title={t('balise_commands_unseal')}
                                    className="p-1.5 rounded-lg text-yellow-600 hover:bg-yellow-50"><Unlock className="w-4 h-4" /></button>
                                </>
                              )}
                              <button type="button" onClick={() => handleDecommissionClick(b)} title={t('balise_action_decommission', { defaultValue: 'Decommission' })}
                                className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Archive className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {totalCount > PAGE_SIZE && (
                <div className="p-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
                  <p className="text-sm text-gray-500">
                    {t('balise_pagination_showing', { defaultValue: 'Showing {{from}}-{{to}} of {{total}}', from: ((page - 1) * PAGE_SIZE) + 1, to: Math.min(page * PAGE_SIZE, totalCount), total: totalCount })}
                  </p>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={page <= 1 || inventoryLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">
                      <ChevronLeft className="w-4 h-4" /> {t('balise_pagination_prev')}
                    </button>
                    <button type="button" disabled={page >= totalPages || inventoryLoading} onClick={() => setPage((p) => p + 1)}
                      className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">
                      {t('balise_pagination_next')} <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </>
    )
  }

  // Statistics card row — global counts, shared across both list tabs
  // so the operator doesn't lose context when switching between
  // Jointech/MaxVision and JIMI views. Each card acts as a filter:
  // clicking toggles the corresponding list filter (Total resets both).
  const applyCardFilter = (assignment: string, operational: string) => {
    const alreadyActive = statusFilter === assignment && operationalFilter === operational
    setStatusFilter(alreadyActive ? '' : assignment)
    setOperationalFilter(alreadyActive ? '' : operational)
  }

  const renderStatsRow = () => {
    const cardClass = (active: boolean, palette: string) =>
      `rounded-xl border p-4 flex items-center gap-3 text-left w-full transition-shadow cursor-pointer hover:shadow-md ${palette} ${
        active ? 'ring-2 ring-blue-500 shadow-md' : ''
      }`
    const noFilter = !statusFilter && !operationalFilter
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button type="button" onClick={() => { setStatusFilter(''); setOperationalFilter('') }}
          className={cardClass(false, 'border-blue-100 bg-blue-50')}
          title={t('balise_stat_filter_all_hint', { defaultValue: 'Show all balises' }) as string}>
          <Cpu className="w-8 h-8 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">{t('balise_stat_total')}</p>
            <p className="text-2xl font-semibold text-gray-900">{statistics.total}</p>
          </div>
        </button>
        <button type="button" onClick={() => applyCardFilter('ASSIGNED', '')}
          className={cardClass(!noFilter && statusFilter === 'ASSIGNED', 'border-green-100 bg-green-50')}
          title={t('balise_stat_filter_hint', { defaultValue: 'Click to filter the list' }) as string}>
          <Package className="w-8 h-8 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">{t('balise_stat_assigned', { defaultValue: 'Assigned' })}</p>
            <p className="text-2xl font-semibold text-gray-900">{statistics.assigned}</p>
          </div>
        </button>
        <button type="button" onClick={() => applyCardFilter('AVAILABLE', '')}
          className={cardClass(!noFilter && statusFilter === 'AVAILABLE', 'border-orange-100 bg-orange-50')}
          title={t('balise_stat_filter_hint', { defaultValue: 'Click to filter the list' }) as string}>
          <AlertTriangle className="w-8 h-8 text-orange-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">{t('balise_stat_unassigned', { defaultValue: 'Unassigned' })}</p>
            <p className="text-2xl font-semibold text-gray-900">{statistics.unassigned}</p>
          </div>
        </button>
        <button type="button" onClick={() => applyCardFilter('', 'ACTIVE')}
          className={cardClass(!noFilter && operationalFilter === 'ACTIVE', 'border-emerald-100 bg-emerald-50')}
          title={t('balise_stat_filter_hint', { defaultValue: 'Click to filter the list' }) as string}>
          <Signal className="w-8 h-8 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">{t('balise_stat_active')}</p>
            <p className="text-2xl font-semibold text-gray-900">{statistics.online}</p>
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-100"><Radio className="w-5 h-5 text-blue-600" /></div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t('balise_mgmt_title')}</h1>
            <p className="text-sm text-gray-500">{t('balise_mgmt_subtitle')}</p>
          </div>
        </div>
        <button type="button" onClick={loadInventory} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <RefreshCw className="w-4 h-4" /> {t('balise_refresh_list')}
        </button>
      </div>

      <div className="flex-shrink-0 flex border-b border-gray-200 bg-white px-2">
        {tabs.map((tab) => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <div className="flex-1 min-h-0 overflow-auto p-4">
          <BMDashboard showMessage={showMessage} />
        </div>
      )}

      {activeTab === 'inventory' && (
        <div className="flex-1 min-h-0 flex flex-col p-4 gap-3">
          {renderBaliseList()}
          <div className="flex-shrink-0">{renderStatsRow()}</div>
        </div>
      )}

      {/* Details Modal — STRICTLY READ-ONLY (legacy parity).
          4 cards (Device / Assignment / Technical / Location) + recent
          activity panel + BaliseSyncControl widget. No row-action
          duplicates inside; the operator triggers operations from the
          inventory row icons. The sync widget stays because sync
          state is a separate concern from assignment state and isn't
          duplicated by any row icon. */}
      {detailBalise && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40" onClick={() => setDetailBalise(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <h2 className="text-lg font-semibold text-gray-900">{t('balise_details_title')}</h2>
              <button type="button" onClick={() => setDetailBalise(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('detail_card_device', { defaultValue: 'Device information' })}</h3>
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_field_device_name')}</dt>
                    <dd className="font-medium text-gray-900 text-right break-all">{detailBalise.deviceName || detailBalise.universalId || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_field_universal_id')}</dt>
                    <dd className="font-mono text-gray-900 text-right break-all">{detailBalise.universalId || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_col_brand')}</dt>
                    <dd className="text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold uppercase tracking-wide ${brandBadgeClass(detailBalise.brand)}`}>
                        {brandLabel(detailBalise.brand)}
                      </span>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_col_lock_code')}</dt>
                    <dd className="font-mono font-semibold text-gray-900 text-right break-all">{detailBalise.lockCode}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_col_status')}</dt>
                    <dd className="text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${statusBadgeClass(detailBalise.status || '')}`}>
                        {detailBalise.status || '—'}
                      </span>
                    </dd>
                  </div>
                  {detailBalise.deviceType && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('balise_field_device_type', { defaultValue: 'Device type' })}</dt>
                      <dd className="font-mono text-gray-900 text-right">{detailBalise.deviceType}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('detail_card_assignment', { defaultValue: 'Assignment' })}</h3>
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_field_assignment')}</dt>
                    <dd className="text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${assignmentBadgeClass(!!detailBalise.isAssigned)}`}>
                        {detailBalise.assignmentStatus || (detailBalise.isAssigned ? t('balise_assigned') : t('balise_unassigned'))}
                      </span>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_col_customer')}</dt>
                    <dd className="font-medium text-gray-900 text-right break-all">{detailBalise.customerName || t('balise_customer_unassigned')}</dd>
                  </div>
                  {detailBalise.containerId != null && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('detail_field_container_id', { defaultValue: 'Container ID' })}</dt>
                      <dd className="font-mono text-gray-900 text-right">{String(detailBalise.containerId)}</dd>
                    </div>
                  )}
                  {detailBalise.assetId != null && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('detail_field_asset_id', { defaultValue: 'Asset ID' })}</dt>
                      <dd className="font-mono text-gray-900 text-right">{String(detailBalise.assetId)}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('detail_card_technical', { defaultValue: 'Technical' })}</h3>
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_col_battery')}</dt>
                    <dd className="flex items-center gap-2">
                      <div className="relative w-[60px] h-3 bg-gray-200 rounded overflow-hidden border border-gray-300">
                        <div className={`absolute left-0 top-0 h-full ${batteryBarColor(detailBalise.batteryLevel)}`}
                          style={{ width: `${Math.min(100, Math.max(0, detailBalise.batteryLevel ?? 0))}%` }} />
                      </div>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {detailBalise.batteryLevel != null ? `${detailBalise.batteryLevel}%` : t('balise_battery_unknown')}
                      </span>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_field_last_seen')}</dt>
                    <dd className="font-medium text-gray-900 text-right">{detailBalise.lastSeen ? new Date(detailBalise.lastSeen).toLocaleString() : t('not_provided')}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">{t('balise_field_created', { defaultValue: 'Created' })}</dt>
                    <dd className="font-medium text-gray-900 text-right">{detailBalise.createdAt ? new Date(detailBalise.createdAt).toLocaleString() : t('not_provided')}</dd>
                  </div>
                </dl>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('detail_card_location', { defaultValue: 'Location' })}</h3>
                {detailBalise.latitude != null && detailBalise.longitude != null ? (
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('detail_field_coords', { defaultValue: 'Coordinates' })}</dt>
                      <dd className="font-mono text-gray-900 text-right select-all">
                        {Number(detailBalise.latitude).toFixed(6)}, {Number(detailBalise.longitude).toFixed(6)}
                      </dd>
                    </div>
                    <div className="text-[11px] text-gray-500 italic mt-2">
                      {t('detail_location_hint', { defaultValue: 'Use the GPS or Trajectory row icons to view on a map.' })}
                    </div>
                  </dl>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {t('detail_location_none', { defaultValue: 'No GPS data available yet.' })}
                  </p>
                )}
              </div>
            </div>

            {/* Assignment history — where has this device been mounted */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-600" />
                  {t('balise_assignment_history_title', { defaultValue: 'Assignment history' })}
                </h3>
                {detailHistoryLoading && (
                  <span className="text-xs text-gray-400">{t('loading')}</span>
                )}
              </div>
              {!detailHistoryLoading && detailHistory.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-2">
                  {t('balise_no_assignment_history', { defaultValue: 'This balise has never been assigned to a vehicle or container.' })}
                </div>
              ) : detailHistory.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        <th className="py-1.5 pr-3">{t('balise_history_target', { defaultValue: 'Assigned to' })}</th>
                        <th className="py-1.5 pr-3">{t('balise_col_customer')}</th>
                        <th className="py-1.5 pr-3">{t('balise_history_from', { defaultValue: 'From' })}</th>
                        <th className="py-1.5 pr-3">{t('balise_history_to', { defaultValue: 'To' })}</th>
                        <th className="py-1.5">{t('balise_col_status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detailHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td className="py-1.5 pr-3 font-medium text-gray-900">
                            {entry.container_name || [entry.asset_code, entry.license_plate].filter(Boolean).join(' · ') || '—'}
                            <span className="ml-1 text-[10px] text-gray-400 uppercase">{entry.assignment_type}</span>
                          </td>
                          <td className="py-1.5 pr-3 text-gray-600">{entry.customer_name || '—'}</td>
                          <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">{entry.assignment_date ? new Date(entry.assignment_date).toLocaleString() : '—'}</td>
                          <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">{entry.unassignment_date ? new Date(entry.unassignment_date).toLocaleString() : '—'}</td>
                          <td className="py-1.5">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium ${entry.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {entry.is_active ? t('balise_history_active', { defaultValue: 'Active' }) : t('balise_history_ended', { defaultValue: 'Ended' })}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sync widget — separate concern from assignment state.
                Shows current sync ACTIVE/INACTIVE/MAINTENANCE plus
                Activate / Deactivate / Maintain buttons. */}
            <BaliseSyncControl lockCode={detailBalise.lockCode} showMessage={showMessage} />

            {/* Recent activity (24h) — unified across brands. Powered by
                GET /api/balises/:lockCode/recent-events. For JIMI rows in
                particular this is the FIRST place ops can see that the
                bridge is actually delivering events for a given device,
                because the legacy trajectory endpoints filter on
                latitude IS NOT NULL. */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <Activity className="w-4 h-4 text-blue-600" />
                  {t('balise_recent_activity_title', { defaultValue: 'Recent activity (24h)' })}
                </h3>
                {recentEventsLoading && (
                  <span className="text-xs text-gray-400">{t('loading')}</span>
                )}
              </div>
              {recentEventsError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                  {recentEventsError}
                </div>
              )}
              {!recentEventsLoading && !recentEventsError && recentEvents && (
                <>
                  {recentEvents.summary.totalEvents === 0 ? (
                    <div className="text-xs text-gray-500 italic py-2">
                      {t('balise_no_recent_events', { defaultValue: 'No events in the last 24 hours.' })}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                          {recentEvents.summary.totalEvents}{' '}
                          {t('balise_events_total', { defaultValue: 'events' })}
                        </span>
                        {recentEvents.summary.gpsEvents > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-100">
                            {recentEvents.summary.gpsEvents} GPS
                          </span>
                        )}
                        {Object.entries(recentEvents.summary.byEventType).map(([type, count]) => (
                          <span
                            key={type}
                            className="px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 text-xs font-mono border border-gray-200"
                            title={type}
                          >
                            {type}: {count}
                          </span>
                        ))}
                      </div>
                      <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
                        {recentEvents.events.map((ev) => (
                          <div
                            key={ev.id}
                            className="flex items-start gap-2 px-2 py-1.5 text-xs hover:bg-gray-50"
                          >
                            <span className="text-gray-400 font-mono shrink-0 tabular-nums">
                              {new Date(ev.eventTimestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                            <span className="font-medium text-gray-900 shrink-0">{ev.eventType}</span>
                            <span className="text-gray-400 ml-auto shrink-0 font-mono">
                              #{ev.sourceEventId}
                            </span>
                            {ev.hasGps && (
                              <MapPin className="w-3 h-3 text-emerald-500 shrink-0" aria-label="has GPS" />
                            )}
                          </div>
                        ))}
                      </div>
                      {recentEvents.summary.lastEventAt && (
                        <p className="mt-1.5 text-[11px] text-gray-400">
                          {t('balise_last_event_at', { defaultValue: 'Last event' })}:{' '}
                          {new Date(recentEvents.summary.lastEventAt).toLocaleString()}
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <button type="button" onClick={() => setDetailBalise(null)} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">{t('close')}</button>
          </div>
        </div>
      )}

      {/* SIM picker modal (row icon #3). Real picker, replaces the
          legacy Ionic "coming soon" toast. */}
      {simTarget && (
        <SimAssignModal
          universalId={simTarget.universalId || simTarget.lockCode}
          deviceLabel={simTarget.deviceName || simTarget.universalId || simTarget.lockCode}
          currentSim={null}
          showMessage={showMessage}
          onClose={() => setSimTarget(null)}
          onChanged={() => loadInventory()}
        />
      )}

      {/* MapModal — single component handles GPS Location iframe and
          Trajectory polyline. Initial mode comes from which row icon
          was clicked; the operator can toggle inside the modal. */}
      {mapTarget && (
        <MapModal
          balise={{
            id: mapTarget.id,
            universalId: mapTarget.universalId,
            lockCode: mapTarget.lockCode,
            brand: mapTarget.brand,
            deviceName: mapTarget.deviceName,
            latitude: mapTarget.latitude ?? null,
            longitude: mapTarget.longitude ?? null,
            lastSeen: mapTarget.lastSeen ?? null,
            status: mapTarget.status ?? null,
            isAssigned: !!(mapTarget.isAssigned || mapTarget.assetId || mapTarget.containerId),
          }}
          mode={mapMode}
          onClose={() => setMapTarget(null)}
        />
      )}

      {/* Container/Asset Assignment Modal — legacy 3-card layout
          (Available Containers + Available Assets + Create New).
          Single click on a row → confirmation alert (assignTarget) →
          actual assign call. The "Create New" card defers to the
          existing Customer Management UI for the multi-step
          customer-then-asset-or-container flow; we surface a hint
          rather than reproducing it here. */}
      {showContainerAssignModal && assignBalise && (() => {
        // One search box drives BOTH lists; the status dropdown narrows both
        // too. Much friendlier than the old tiny per-card search inputs.
        const q = containerSearch.trim().toLowerCase()
        const statuses = [...new Set([
          ...availableContainers.map((c) => String(c.status || '').toUpperCase()),
          ...availableAssets.map((a) => String(a.status || '').toUpperCase()),
        ].filter(Boolean))].sort()
        const matchesStatus = (s: any) => !assignStatusFilter || String(s || '').toUpperCase() === assignStatusFilter
        const filteredContainers = availableContainers.filter((c) =>
          matchesStatus(c.status) && (!q ||
            String(c.name || '').toLowerCase().includes(q) ||
            String(c.container_type || '').toLowerCase().includes(q) ||
            String(c.owner || '').toLowerCase().includes(q) ||
            String(c.destination || '').toLowerCase().includes(q)),
        )
        const filteredAssets = availableAssets.filter((a) =>
          matchesStatus(a.status) && (!q ||
            String(a.name || '').toLowerCase().includes(q) ||
            String(a.type || '').toLowerCase().includes(q) ||
            String(a.identifier || '').toLowerCase().includes(q) ||
            String(a.location || '').toLowerCase().includes(q)),
        )
        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[88vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('balise_assign_modal_title_v2', { defaultValue: 'Assign balise' })} — {assignBalise.universalId}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('balise_assign_modal_subtitle', { defaultValue: 'Pick a container or an asset.' })}
                  </p>
                </div>
                <button type="button" onClick={() => { setShowContainerAssignModal(false); setContainerSearch(''); setAssetSearch(''); setAssignStatusFilter('') }} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
              </div>

              {/* Global search + status filter */}
              <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-gray-100">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    value={containerSearch}
                    onChange={(e) => setContainerSearch(e.target.value)}
                    placeholder={t('balise_assign_search_all', { defaultValue: 'Search containers and assets by name, type, owner, location…' }) as string}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {statuses.length > 1 && (
                  <select
                    value={assignStatusFilter}
                    onChange={(e) => setAssignStatusFilter(e.target.value)}
                    className="px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('all_statuses', { defaultValue: 'All statuses' })}</option>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {filteredContainers.length + filteredAssets.length} / {availableContainers.length + availableAssets.length}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Card 1: Available Containers */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 flex-1">
                      {t('balise_assign_card_containers', { defaultValue: 'Available containers' })} ({filteredContainers.length})
                    </h3>
                  </div>
                  <div className="max-h-[38vh] overflow-y-auto p-2">
                    {containersLoading ? (
                      <div className="text-center py-6 text-sm text-gray-500">{t('loading')}</div>
                    ) : filteredContainers.length === 0 ? (
                      <div className="text-center py-6 text-sm text-gray-500 italic">
                        {availableContainers.length === 0
                          ? t('balise_no_containers', { defaultValue: 'No containers available' })
                          : t('balise_no_match_containers', { defaultValue: 'No containers match your search.' })}
                      </div>
                    ) : (
                      <ul className="space-y-1">
                        {filteredContainers.map((c) => (
                          <li key={`c-${c.id}`}>
                            <button
                              type="button"
                              onClick={() => requestAssignTarget('container', c.id, c.name)}
                              className="w-full text-left p-2 rounded-md border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Package className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate">{c.name}</div>
                                  <div className="text-[11px] text-gray-500 truncate">
                                    {[c.container_type, c.status, c.owner, c.destination].filter(Boolean).join(' · ') || '—'}
                                  </div>
                                </div>
                                {c.balise_assigned && (
                                  <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                                    {c.balise_count || 1} {t('balise_assigned_short', { defaultValue: 'Balise' })}
                                  </span>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Card 2: Available Assets */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 flex-1">
                      {t('balise_assign_card_assets', { defaultValue: 'Available assets' })} ({filteredAssets.length})
                    </h3>
                  </div>
                  <div className="max-h-[38vh] overflow-y-auto p-2">
                    {containersLoading ? (
                      <div className="text-center py-6 text-sm text-gray-500">{t('loading')}</div>
                    ) : filteredAssets.length === 0 ? (
                      <div className="text-center py-6 text-sm text-gray-500 italic">
                        {availableAssets.length === 0
                          ? t('balise_no_assets', { defaultValue: 'No assets available' })
                          : t('balise_no_match_assets', { defaultValue: 'No assets match your search.' })}
                      </div>
                    ) : (
                      <ul className="space-y-1">
                        {filteredAssets.map((a) => (
                          <li key={`a-${a.id}`}>
                            <button
                              type="button"
                              onClick={() => requestAssignTarget('asset', a.id, a.name)}
                              className="w-full text-left p-2 rounded-md border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Cpu className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate">{a.name}</div>
                                  <div className="text-[11px] text-gray-500 truncate">
                                    {[a.type, a.status, a.identifier, a.location].filter(Boolean).join(' · ') || '—'}
                                  </div>
                                </div>
                                {a.balise_assigned && (
                                  <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">
                                    {t('balise_assigned')}
                                  </span>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Card 3: Create new — two buttons open customer picker, then inline form */}
                <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Plus className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-900 flex-1">
                      {t('balise_assign_card_create', { defaultValue: 'Create New Asset/Container' })}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {t('balise_assign_create_hint', { defaultValue: 'No suitable container? Create a new one for this balise:' })}
                  </p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => openCreationWizard('asset')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
                      <Cpu className="w-3.5 h-3.5" /> {t('balise_create_asset', { defaultValue: 'CREATE ASSET' })}
                    </button>
                    <button type="button" onClick={() => openCreationWizard('container')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 border-blue-500 text-blue-700 bg-blue-50 hover:bg-blue-100">
                      <Package className="w-3.5 h-3.5" /> {t('balise_create_container', { defaultValue: 'CREATE CONTAINER' })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Assignment confirmation alert (legacy parity) */}
      {showContainerAssignModal && assignBalise && assignTarget && (
        <div className="fixed inset-0 z-[310] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 space-y-3">
            <h3 className="text-base font-semibold text-gray-900">
              {t('balise_assign_confirm_title', { defaultValue: 'Confirm assignment' })}
            </h3>
            <div className="text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-md p-3 font-mono break-all">
              {assignBalise.universalId} → {assignTarget.name}
            </div>
            <div className="text-xs text-gray-500">
              {assignTarget.kind === 'container'
                ? t('balise_assign_confirm_container', { defaultValue: 'This balise will be assigned to the selected container.' })
                : t('balise_assign_confirm_asset', { defaultValue: 'This balise will be assigned to the selected asset.' })}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAssignTarget(null)} disabled={assignBusy} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">{t('cancel')}</button>
              <button type="button" onClick={confirmAssignTarget} disabled={assignBusy} className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
                {assignBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {assignBusy ? t('loading', { defaultValue: 'Assigning...' }) : t('balise_assign_button', { defaultValue: 'Assign' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Confirmation — sync-aware (legacy §C). 2 buttons
          when sync is INACTIVE/unknown, 3 buttons when ACTIVE so the
          operator decides whether to keep sync running or stop it. */}
      {showUnassignConfirm && unassignBalise && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex gap-3"><AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" /><div>
              <h3 className="text-lg font-semibold text-gray-900">{t('balise_unassign_confirm_title', { defaultValue: 'Confirm Unassignment' })}</h3>
              {unassignSyncStatus === null ? (
                <p className="mt-2 text-sm text-gray-500 italic">{t('balise_unassign_checking_sync', { defaultValue: 'Checking sync status…' })}</p>
              ) : unassignSyncStatus === 'ACTIVE' ? (
                <p className="mt-2 text-sm text-gray-700">
                  {t('sync_stop_message', {
                    defaultValue: 'Sync is currently ACTIVE for {{id}}. What should happen?',
                    id: unassignBalise.universalId,
                  })}
                </p>
              ) : (
                <p className="mt-2 text-sm text-gray-600">
                  {t('balise_unassign_confirm_msg_v2', {
                    defaultValue: 'Unassign {{id}} from {{name}}?',
                    id: unassignBalise.universalId,
                    name: unassignVehicleLabel || unassignBalise.customerName || t('balise_customer_unassigned'),
                  })}
                </p>
              )}
              {unassignVehicleLabel && (
                <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                  {t('balise_unassign_vehicle_warning', {
                    defaultValue: '{{vehicle}} will no longer be tracked after this.',
                    vehicle: unassignVehicleLabel,
                  })}
                </p>
              )}
            </div></div>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeUnassignConfirm} disabled={unassignBusy}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                {t('cancel')}
              </button>
              {unassignSyncStatus === 'ACTIVE' && (
                <>
                  <button type="button" onClick={() => performUnassign('maintain')} disabled={unassignBusy}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
                    {t('sync_stop_keep', { defaultValue: 'Keep sync, unassign' })}
                  </button>
                  <button type="button" onClick={() => performUnassign('deactivate')} disabled={unassignBusy}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
                    {t('sync_stop_stop', { defaultValue: 'Stop sync, unassign' })}
                  </button>
                </>
              )}
              {unassignSyncStatus !== 'ACTIVE' && unassignSyncStatus !== null && (
                <button type="button" onClick={() => performUnassign(null)} disabled={unassignBusy}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
                  {unassignBusy ? t('balise_action_in_progress') : t('balise_action_unassign')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Decommission Confirmation — legacy textarea-reason alert
          (replaces the old "type the lock code" gate). The reason is
          captured for the audit log; the explicit typed-confirmation
          was overkill since a decommissioned device can be re-onboarded. */}
      {showDecomConfirm && decomBalise && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex gap-3"><AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" /><div>
              <h3 className="text-lg font-semibold text-gray-900">{t('balise_decom_confirm_title', { defaultValue: 'Decommission balise' })}</h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('balise_decom_confirm_msg_v2', {
                  defaultValue: 'Decommission {{id}}? This is permanent.',
                  id: decomBalise.universalId,
                })}
              </p>
            </div></div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('balise_decom_reason', { defaultValue: 'Reason' })}</label>
              <textarea
                value={decomReason}
                onChange={(e) => setDecomReason(e.target.value.slice(0, 200))}
                rows={3}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                placeholder={t('balise_decom_reason_placeholder', { defaultValue: 'Reason for decommissioning…' }) as string}
              />
              <div className="text-[11px] text-gray-400 text-right mt-0.5">{decomReason.length}/200</div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowDecomConfirm(false)} disabled={decomBusy}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                {t('cancel')}
              </button>
              <button type="button" onClick={confirmDecommission} disabled={decomBusy}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
                {decomBusy ? t('balise_action_in_progress') : t('balise_decom_confirm', { defaultValue: 'Decommission' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seal/Unseal Command Modal */}
      {showCommandModal && commandBalise && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-start">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {commandType === 'seal' ? <Lock className="w-5 h-5 text-green-600" /> : <Unlock className="w-5 h-5 text-yellow-600" />}
                {commandType === 'seal' ? t('balise_commands_seal') : t('balise_commands_unseal')} — {commandBalise.brand}
              </h2>
              <button type="button" onClick={() => { setShowCommandModal(false); setCommandPassword('') }} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">{t('balise_field_device_name')}</span><span className="font-medium">{commandBalise.deviceName || commandBalise.universalId}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">{t('balise_col_lock_code')}</span><span className="font-mono font-semibold">{commandBalise.lockCode}</span></div>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('balise_password_label', { defaultValue: 'Key Password (optional)' })}</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={commandPassword} onChange={(e) => setCommandPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm pr-10" placeholder={t('balise_password_placeholder', { defaultValue: 'Enter password...' })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
            {commandBalise.brand?.toUpperCase().includes('MAXVISION') && (
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={commandForTrip} onChange={(e) => setCommandForTrip(e.target.checked)} className="rounded border-gray-300 text-blue-600" />{t('balise_for_trip', { defaultValue: 'For trip mode' })}</label>
            )}
            {commandBalise.brand?.toUpperCase().includes('JOINTECH') && (
              <p className="text-xs text-gray-500">{t('balise_jointech_password_hint', { defaultValue: 'Leave the password empty to use 000000, the password these JT701 locks were built with.' })}</p>
            )}
            {(() => {
              const seen = commandBalise.lastSeen ? new Date(commandBalise.lastSeen) : null
              const offline = !seen || Number.isNaN(seen.getTime()) || Date.now() - seen.getTime() > 15 * 60 * 1000
              if (!offline) return null
              return (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {t('balise_lock_offline', {
                    defaultValue: 'This lock last reported {{when}}. It is not connected, so the command cannot open or close it.',
                    when: seen ? seen.toLocaleString() : t('never', { defaultValue: 'never' })
                  })}
                </div>
              )
            })()}
            <div className={`p-3 rounded-lg text-sm ${commandType === 'seal' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}>
              <strong>{commandType === 'seal' ? t('balise_commands_seal') : t('balise_commands_unseal')}</strong> — {commandType === 'seal' ? t('balise_seal_description', { defaultValue: 'This will lock the device.' }) : t('balise_unseal_description', { defaultValue: 'This will unlock the device.' })}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowCommandModal(false); setCommandPassword('') }} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">{t('cancel')}</button>
              <button type="button" onClick={handleSendCommand} disabled={commandBusy}
                className={`px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${commandType === 'seal' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}>
                {commandBusy ? t('balise_action_in_progress') : (commandType === 'seal' ? t('balise_send_seal', { defaultValue: 'Send Lock' }) : t('balise_send_unseal', { defaultValue: 'Send Unlock' }))}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreationWizard && (
        <VehicleCreationWizard
          isOpen={showCreationWizard}
          initialSource={wizardSource}
          onClose={() => setShowCreationWizard(false)}
          showMessage={showMessage}
          onSuccess={() => {
            setShowCreationWizard(false)
            void refreshAssignLists()
          }}
        />
      )}
    </div>
  )
}

export default BaliseManagement
